import { perkpointApi } from "./client";

export type CreateProgramTierThreshold = {
  metric: string;
  entry_value: number;
  retention_value: number | null;
};

export type CreateProgramTierBenefit = {
  name: string;
  value: string;
};

export type CreateProgramTierPayload = {
  key: string;
  name: string;
  rank: number;
  tier_type: string;
  invite_only: boolean;
  display_color: string | null;
  description: string | null;
  earn_rate: number;
  welcome_bonus_points: number | null;
  benefits: CreateProgramTierBenefit[];
  thresholds: CreateProgramTierThreshold[];
};

export type PerkPointTierApiItem = {
  id: string;
  key: string;
  name: string;
  rank: number;
  tier_type: string;
  invite_only: boolean;
  display_color: string | null;
  description: string | null;
  earn_rate: number | null;
  welcome_bonus_points: number | null;
  benefits: CreateProgramTierBenefit[];
  thresholds: CreateProgramTierThreshold[];
};

export type PerkPointTiersSummary = {
  program_id: string;
  total_tiers: number;
  tier_range: {
    from: string;
    to: string;
    from_key: string;
    to_key: string;
    label: string;
  };
  members_enrolled: number;
  members_enrolled_label: string;
  gold_plus: {
    from_tier_key: string;
    from_tier_name: string;
    members: number;
    percent: number;
    label: string;
  };
  top_tier: {
    id: string;
    key: string;
    name: string;
    rank: number;
    members: number;
  } | null;
};

export const tierKeys = {
  all: ["perkpoint", "tiers"] as const,
  list: (programId: string) =>
    ["perkpoint", "tiers", programId, "list"] as const,
  summary: (programId: string) =>
    ["perkpoint", "tiers", programId, "summary"] as const,
};

const base = (programId: string) => `/admin/programs/${programId}/tiers`;

function unwrapTiers(data: unknown): PerkPointTierApiItem[] {
  if (Array.isArray(data)) return data as PerkPointTierApiItem[];
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    for (const key of ["data", "tiers", "items"]) {
      if (Array.isArray(record[key])) {
        return record[key] as PerkPointTierApiItem[];
      }
    }
  }
  return [];
}

export type UpdateProgramTierPayload = Partial<
  Omit<CreateProgramTierPayload, "key">
> & {
  invite_only?: boolean;
};

export const TiersApi = {
  async create(programId: string, payload: CreateProgramTierPayload) {
    const { data } = await perkpointApi.post(base(programId), payload);
    return data;
  },

  async update(
    programId: string,
    tierId: string,
    payload: UpdateProgramTierPayload,
  ) {
    const { data } = await perkpointApi.patch(
      `${base(programId)}/${tierId}`,
      payload,
    );
    return data;
  },

  async list(programId: string) {
    const { data } = await perkpointApi.get(base(programId));
    return unwrapTiers(data);
  },

  async summary(programId: string) {
    const { data } = await perkpointApi.get<PerkPointTiersSummary>(
      `${base(programId)}/summary`,
    );
    return data;
  },
};
